import isEmpty from "lodash-es/isEmpty";

export const POS_AUTH_SESSION_KEYS = Object.freeze({
  TOKEN: "token",
  POS_TOKEN: "posToken",
  CLIENT_TYPE: "clientType",
  POS_TYPE: "posType",
  OUTLET_ID: "outletId",
  CLIENT_DATA: "clientData",
  POS_SESSION_ID: "posSessionId",
});

// Safari with "Prevent Cross-Site Tracking" blocks every cookie read and write
// from a third-party iframe, so the POS session is carried here instead. An
// in-memory Map alone is not enough: it dies on any full document load, and the
// petpooja flow takes several (the /RedirectHandler bounce into hp-web, plus the
// window.location navigations both apps use throughout). sessionStorage is the
// carrier because Safari partitions third-party storage by top-level site rather
// than blocking it outright, and that partition is exactly the scope we want.
//
// The key and the payload shape are shared with hp-web: in production both apps
// are served from www.hyperpure.com and therefore read the same partitioned
// storage, so the two copies of this file must stay in step.
const STORAGE_KEY = "hpPosAuth";

// Storage access throws outright under some Safari privacy configurations, and
// this module is also evaluated during SSR where there is no window at all.
function getStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  } catch (e) {
    return null;
  }
}

class PosAuthManager {
  constructor() {
    this._data = new Map();
    this._hydrate();
  }

  // Deliberately avoids isEmpty(): this runs while the module is still being
  // evaluated and helpers.js imports this file, so that binding is not
  // guaranteed to be initialised yet. Nothing is lost by it — every persisted
  // value already passed isEmpty() on the way in, and the check below only
  // guards against a hand-edited or truncated payload.
  _hydrate() {
    const storage = getStorage();
    if (!storage) return;

    let persisted = null;
    try {
      persisted = JSON.parse(storage.getItem(STORAGE_KEY));
    } catch (e) {
      return;
    }
    if (!persisted || typeof persisted !== "object") return;

    for (const key of Object.values(POS_AUTH_SESSION_KEYS)) {
      const value = persisted[key];
      if (value !== null && value !== undefined && value !== "") {
        this._data.set(key, value);
      }
    }
  }

  _persist() {
    const storage = getStorage();
    if (!storage) return;
    try {
      if (this._data.size === 0) {
        storage.removeItem(STORAGE_KEY);
        return;
      }
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify(Object.fromEntries(this._data)),
      );
    } catch (e) {
      // Quota exceeded or a blocked store. The Map still serves this document;
      // only survival across the next navigation is lost.
    }
  }

  _write(key, value) {
    if (isEmpty(value)) {
      this._data.delete(key);
      return;
    }
    this._data.set(key, value);
  }

  get(key) {
    return this._data.get(key) ?? null;
  }

  set(key, value) {
    this._write(key, value);
    this._persist();
  }

  setMany(entries = {}) {
    for (const [key, value] of Object.entries(entries)) {
      this._write(key, value);
    }
    this._persist();
  }

  has(key) {
    return this._data.has(key);
  }

  // Also drops the persisted copy — a session cleared on 401 or at sign-out must
  // not come back on the next reload.
  clear() {
    this._data.clear();
    this._persist();
  }

  keys() {
    return [...this._data.keys()];
  }
}

const posAuthManager = new PosAuthManager();
export default posAuthManager;

export function getAuthTokenFromPosAuthManager() {
  return (
    posAuthManager.get(POS_AUTH_SESSION_KEYS.TOKEN) ||
    posAuthManager.get(POS_AUTH_SESSION_KEYS.POS_TOKEN) ||
    null
  );
}
